import { z } from "zod";
import { OpModeLiveData } from "./opmode.js";
import { zAny, zArr, zBool, zNullable, zNum, zObj, zOptional, zStr } from "./zodWrapper.js";
/**
 * WARNING!
 * these type defitinions was reverse-engineered by looking at data coming from the BW API
 * from time to time, BW API sends data that does not match these ¯\_(ツ)_/¯
 */
export const Coordinates = z.tuple([zNum(), zNum()]);
export const Locality = zObj({
    aquaCultureRegistryVersion: zNum(),
    localityNo: zNum(),
    name: zStr(),
    municipalityNo: zStr(),
    municipality: zStr(),
});
export const Localities = zArr(Locality);
export const IntervalPoint = zObj({
    cog: zNullable(zNum()),
    heading: zNullable(zNum()),
    lat: zNum(),
    lon: zNum(),
    msgt: zStr(),
    rot: zNullable(zNum()),
    sog: zNullable(zNum()),
});
export const Track = zObj({
    id: zNum(),
    fromTime: zStr(),
    toTime: zStr(),
    isNoSignal: zBool(),
    intervalPoints: zArr(IntervalPoint),
    wellBoatWeekId: zNum(),
    coordinates: zArr(Coordinates),
});
export const BwVesselTrackData = zObj({
    cog: zNullable(zNum()),
    sog: zNullable(zNum()),
    coordinates: zArr(zNum()),
    fromDate: zNullable(zStr()),
    toDate: zNullable(zStr()),
    tracks: zArr(Track),
});
export const BwVesselData = zObj({
    a: zNum(),
    b: zNum(),
    c: zNum(),
    callsign: zStr(),
    country: zStr(),
    d: zNum(),
    destination: zNullable(zStr()),
    draught: zNullable(zNum()),
    eta: zNullable(zStr()),
    imo: zNullable(zNum()),
    isWellBoat: zBool(),
    mmsi: zNum(),
    name: zStr(),
    shipRegisterVesselType: zNullable(zStr()),
    shipRegisterVesselTypeNameEn: zNullable(zStr()),
    shipRegisterVesselTypeNameNo: zNullable(zStr()),
    shipType: zNum(),
    timeStamp: zStr(),
    track: zOptional(BwVesselTrackData),
});
export const BwPositionData = zObj({
    timeStamp: zStr(),
    cog: zNullable(zNum()),
    sog: zNullable(zNum()),
    rot: zNullable(zNum()),
    navstat: zNum(),
    lat: zNum(),
    lon: zNum(),
    srid: zNum(),
    heading: zNullable(zNum()),
});
export const BwOpenTrackResponse = zObj({
    mmsi: zNum(),
    fromDate: zStr(),
    toDate: zStr(),
    tracks: zArr(Track),
});
export const BwVisitedLocalityVesselTrackResponse = zObj({
    id: zNum(),
    fromTime: zStr(),
    toTime: zStr(),
    isNoSignal: zBool(),
    points: zNullable(zArr(IntervalPoint)),
    vesselTrackWeekId: zNum(),
});
export var DiseaseName;
(function (DiseaseName) {
    DiseaseName["PD"] = "PD";
    DiseaseName["ILA"] = "ILA";
})(DiseaseName || (DiseaseName = {}));
export const DiseaseNameEnum = z.nativeEnum(DiseaseName);
export const BwDiseaseResponse = zObj({
    ruling: zStr(),
    fromDate: zStr(),
    toDate: zStr(),
    diseaseName: DiseaseNameEnum,
});
export const BwLocalityWaypointResponse = zObj({
    name: zStr(),
    localityNo: zNum(),
    isSlaughterHoldingCage: zBool(),
    isJuvenile: zBool(),
    isOnLand: zBool(),
    isBasedOnSurfaceArea: zBool(),
    fromTime: zStr(),
    toTime: zStr(),
    diseases: zArr(BwDiseaseResponse),
});
export var DiseaseZoneType;
(function (DiseaseZoneType) {
    DiseaseZoneType["ILAPROTECTIONZONE"] = "IlaProtectionZone";
    DiseaseZoneType["ILASURVEILLANCEZONE"] = "IlaSurveillanceZone";
    DiseaseZoneType["PDPROTECTIONZONE"] = "PdProtectionZone";
    DiseaseZoneType["PDSURVEILLANCEZONE"] = "PdSurveillanceZone";
    DiseaseZoneType["PDZONE"] = "PdZone";
    DiseaseZoneType["ILAFREEAREA"] = "IlaFreeArea";
})(DiseaseZoneType || (DiseaseZoneType = {}));
export const DiseaseZoneTypeEnum = z.nativeEnum(DiseaseZoneType);
const pointSchema = z.object({
    type: z.string(),
    coordinates: Coordinates,
});
export const BwDiseaseZoneVisitsResponse = zObj({
    diseaseZoneType: DiseaseZoneTypeEnum,
    diseaseZoneId: zStr(),
    fromTime: zNullable(zStr()),
    toTime: zNullable(zStr()),
    fromPoint: pointSchema,
    toPoint: pointSchema,
});
export const BwTrackAnalysisResponse = zObj({
    id: zNum(),
    isBasedOnSurfaceArea: zBool(),
    vesselTrackWeekId: zNum(),
    localityWaypoints: zArr(BwLocalityWaypointResponse),
    hasDiseaseZoneVisitsAnalysis: zBool(),
    diseaseZoneVisits: zArr(BwDiseaseZoneVisitsResponse),
});
/**
 * https://www.barentswatch.no/bwapi/openapi/index.html?urls.primaryName=Fishhealth%20API#/VesselTrackWeek/get_v1_geodata_fishhealth_vesseltrack__mmsi___year___week_
 */
export const BwVisitedLocalityResponse = zObj({
    id: zNum(),
    mmsi: zNum(),
    vesselName: zStr(),
    year: zNum(),
    week: zNum(),
    shipType: zNum(),
    isWellBoat: zBool(),
    shipRegisterVesselType: zNullable(zStr()),
    shipRegisterVesselTypeNameNo: zNullable(zStr()),
    shipRegisterVesselTypeNameEn: zNullable(zStr()),
    vesselTracks: zNullable(zArr(BwVisitedLocalityVesselTrackResponse)),
    trackAnalysis: BwTrackAnalysisResponse,
});
export const PlantInfo = zObj({
    plantId: zNum(),
    condition: zStr(),
    lastLat: zOptional(zNum()),
    lastLon: zOptional(zNum()),
    name: zStr(),
    owner: zStr(),
    vessel: zOptional(z.unknown()),
    position: zOptional(z.unknown()),
    track: zOptional(z.unknown()),
    data: zOptional(zStr()),
    visitedLocations: zOptional(BwVisitedLocalityResponse),
    latestOperationMode: zOptional(OpModeLiveData),
    latestOperationMode2: zOptional(z.object({
        plantId: z.number(),
        opModeName: z.string(),
    })),
});
export const GetDiseaseZonesArgs = zAny();
export const DiseaseZone = zObj({
    id: zNum(),
    version: zNum(),
    forskNr: zNullable(zStr()),
    forskNavn: zNullable(zStr()),
    forskLink: zNullable(zStr()),
    sistEndret: zNullable(zStr()),
    fromDate: zNullable(zStr()),
    toDate: zNullable(zStr()),
    originalDate: zNullable(zStr()),
    geometry: zAny(),
});
export const DiseaseZones = zArr(DiseaseZone);
export const GetFishInfoArgs = zAny();
export const FishInfo = zAny();
export const GetFishFarmOwnersArgs = zAny();
export const FishFarmOwners = zAny();
//# sourceMappingURL=barentsWatch.js.map